import { useState, useEffect, useRef, useMemo } from "react";
import { partners } from "@/mocks/partners";

const PARTNER_TYPES = ["Все", "Отель", "Гостиница", "Хостел", "Пансионат", "Санаторий"];
const STAR_OPTIONS = ["Все", "5", "4"];
const SORT_OPTIONS = [
  { label: "По убыванию рейтинга", value: "stars-desc" },
  { label: "По возрастанию рейтинга", value: "stars-asc" },
  { label: "По дате отзыва", value: "date-desc" },
];

export default function PartnersSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [typeFilter, setTypeFilter] = useState("Все");
  const [starFilter, setStarFilter] = useState("Все");
  const [sortValue, setSortValue] = useState("stars-desc");
  const [page, setPage] = useState(1);
  const perPage = 5;

  const filtered = useMemo(() => {
    let result = [...partners];
    if (typeFilter !== "Все") {
      result = result.filter((p) => p.type === typeFilter);
    }
    if (starFilter !== "Все") {
      result = result.filter((p) => p.stars === parseInt(starFilter));
    }
    if (sortValue === "stars-desc") {
      result.sort((a, b) => b.stars - a.stars);
    } else if (sortValue === "stars-asc") {
      result.sort((a, b) => a.stars - b.stars);
    } else if (sortValue === "date-desc") {
      result.sort((a, b) => new Date(b.reviewDate).getTime() - new Date(a.reviewDate).getTime());
    }
    return result;
  }, [typeFilter, starFilter, sortValue]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, starFilter, sortValue]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [paginated]);

  const renderStars = (count: number) => {
    return Array.from({ length: 5 }, (_, i) => {
      const filled = i < count;
      return (
        <i
          key={i}
          className={`${filled ? "ri-star-fill" : "ri-star-line"} text-accent-500 text-sm`}
        />
      );
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <section
      id="partners"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-background-100"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="reveal opacity-0 inline-block px-4 py-1.5 text-xs font-medium tracking-widest uppercase bg-primary-100 text-primary-700 rounded-full mb-4">
            Нам доверяют
          </span>
          <h2 className="reveal opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-900 mb-4">
            Каталог партнёров
          </h2>
          <p className="reveal opacity-0 text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
            Отзывы и оценки от тех, с кем мы уже работаем
          </p>
        </div>

        {/* Filters */}
        <div className="reveal opacity-0 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
          {/* Type Filter */}
          <div className="flex flex-wrap gap-2">
            {PARTNER_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
                  typeFilter === type
                    ? "bg-primary-500 text-white"
                    : "bg-background-50 text-foreground-600 border border-background-300/60 hover:bg-background-200/70"
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Star Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500">Звёзды:</span>
              <div className="flex gap-1">
                {STAR_OPTIONS.map((star) => (
                  <button
                    key={star}
                    onClick={() => setStarFilter(star)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                      starFilter === star
                        ? "bg-accent-500 text-white"
                        : "bg-background-50 text-foreground-600 border border-background-300/60 hover:bg-background-200/70"
                    }`}
                  >
                    {star === "Все" ? star : `${star} ★`}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort */}
            <select
              value={sortValue}
              onChange={(e) => setSortValue(e.target.value)}
              className="px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Partner Cards */}
        <div className="space-y-4 max-h-[800px] overflow-y-auto pr-1">
          {paginated.map((partner) => (
            <div
              key={partner.id}
              className="reveal opacity-0 flex flex-col md:flex-row gap-5 p-5 md:p-6 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300/60 transition-all duration-300"
            >
              {/* Avatar */}
              <div className="shrink-0 flex flex-col items-center gap-2">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden bg-primary-100 flex items-center justify-center">
                  <img
                    src={partner.avatarUrl}
                    alt={partner.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="text-xs font-medium text-foreground-500 bg-background-200/70 px-2 py-1 rounded-full">
                  {partner.type}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-xl md:text-2xl text-foreground-900">
                    {partner.name}
                  </h3>
                  <div className="flex items-center gap-0.5">
                    {renderStars(partner.stars)}
                  </div>
                </div>
                <p className="text-sm text-foreground-500 mb-3">
                  {partner.description}
                </p>
                <blockquote className="relative pl-4 border-l-2 border-primary-300">
                  <p className="text-sm md:text-base text-foreground-700 italic leading-relaxed">
                    &ldquo;{partner.reviewText}&rdquo;
                  </p>
                </blockquote>
                <p className="text-xs text-foreground-400 mt-2">
                  Отзыв от {formatDate(partner.reviewDate)}
                </p>
              </div>
            </div>
          ))}

          {paginated.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-search-line text-3xl text-foreground-400 mb-3 block" />
              <p className="text-foreground-500">Партнёры не найдены по выбранным фильтрам</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="reveal opacity-0 flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50 border border-background-300/60 text-foreground-600 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-10 h-10 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                  page === p
                    ? "bg-primary-500 text-white"
                    : "bg-background-50 border border-background-300/60 text-foreground-600 hover:bg-background-200/70"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50 border border-background-300/60 text-foreground-600 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}